import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { SgFormGridEntry } from '../../client/components/SgFormGridEntry.js';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { GroupsAPI } from '../../groups/api/GroupsAPI.js';
import { PermissionAPI } from '../../permissions/api/PermissionAPI.js';
import { Permission, Role } from '../../permissions/roles.js';
import { InterviewAPI } from '../../sus-entry-form/interviews/api/InterviewAPI.js';
import { Person } from '../entities/person.js';
import { openClassAdmissionDialog } from './ClassAdmissionDialog.js';
import { openSendSuSUpdateLinkDialog } from './SendSuSUpdateLinkDialog.js';
import {
  getEinschulungsJahr,
  getSuSBewerberStatusDescription,
  isRejectedSuSBewerbung,
  SuSBewerberStatus,
} from './schueler-daten.js';

export const SgStudentInterviewData = stateful((props: { personID: Maybe<string> }) => {
  const user = useObservable(() => currentUser<Person>(), []);

  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({
        _id: 1,
        username: 1,
        email: 1,
        debitorennummer: 1,
        roles: { name: 1, contexts: 1 },
        schuelerDaten: {
          lastGradeGerman: 1,
          lastGradeMath: 1,
          lastGradeSachunterricht: 1,
          potentialMartinusElementarySchoolID: 1,
          hasSiblingsAtMartinusSchools: 1,
          interestInNativeLanguageCourses: 1,
          interestInWholeDaySchooling: 1,
          schoolyearsAfterElementary: 1,
          bewerbungsDate: 1,
          learningDisabilitiesFileID: 1,
          ablehnungGrund: 1,
          sorgerechtsurteilFileID: 1,
          primarySchoolTranscripts: 1,
          learningDisabilitiesFile: selectionSet(() => AdornisFile, 3),
          sorgerechtsurteilFile: selectionSet(() => AdornisFile, 3),
          primarySchoolTranscriptsFiles: selectionSet(() => AdornisFile, 3),
          otherSchool: 1,
          wantedKlassenstufe: 1,
          languageAtHomeIsGerman: 1,
          currentKlassenstufeAtBewerbungsDate: 1,
          motivation: 1,
          schulwechselMotivation: 1,
          participationReligion: 1,
          zuzugsDate: 1,
          internalNotes: 1,
          susBewerberStatus: 1,
        },
      }),
    [props.personID],
  );

  const [wasRejected, setWasRejected] = useState(isRejectedSuSBewerbung(person?.schuelerDaten), [personLoading]);

  const { value: elementarySchool, loading: elementarySchoolLoading } = useQuery(
    () =>
      GroupsAPI.getPublicSchools(
        true,
        person?.schuelerDaten?.potentialMartinusElementarySchoolID ?? 'Andere Grundschule',
      )({
        _id: 1,
        name: 1,
      }),
    [person?.schuelerDaten?.potentialMartinusElementarySchoolID],
  );

  const { value: interviewSlot, loading: interviewSlotLoading } = useQuery(
    () =>
      InterviewAPI.subscribeForChild(props.personID)({
        _id: 1,
        startDate: 1,
        endDate: 1,
        interviewerID: 1,
        childID: 1,
        interviewer: {
          _id: 1,
          profilDaten: {
            stammdaten: {
              vorname: 1,
              nachname: 1,
            },
          },
        },
      }),
    [props.personID],
  );

  if (personLoading || !person || elementarySchoolLoading)
    return html`
      <div class="flex items-center justify-center p-4"><span class="loading loading-spinner"></span></div>
    `;

  const form = useForm(
    person,
    {
      validator: null,
    },
    [],
  );

  const onSave = async () => {
    await guardDialog(
      async () => {
        /**
         * Automatically set status for ablehnung if it is set
         */
        if (
          form.document.schuelerDaten?.ablehnungGrund &&
          form.document.schuelerDaten.susBewerberStatus !== SuSBewerberStatus.ABGELEHNT
        ) {
          form.document.schuelerDaten.susBewerberStatus = SuSBewerberStatus.ABGELEHNT;
        }

        await form.document.save();

        /**
         * Rejecting or withdrawing turns the applicant into an old applicant (and back), which is a role change
         */
        const isRejected = isRejectedSuSBewerbung(form.document.schuelerDaten);
        if (isRejected !== wasRejected) {
          await PermissionAPI.recalculateForPerson(form.document._id)();
          setWasRejected(isRejected);
        }
      },
      {
        success: () => SgSnackbar.show('Schülerdaten erfolgreich gespeichert'),
      },
    );
  };

  if (!user?.hasPermission(Permission.VIEW_SUS_INTERVIEW_DATA)) return nothing;

  return html`
    <div class="card card-body">
      ${CardHeader({
        title: 'Weitere Informationen',
        actions: html`
          ${
            // only a running application can be corrected, a rejected one has left the applicant role
            person.roles.some(role => role.name === Role.SUS_BEWERBER)
              ? html`<button
                    class="btn btn-outline btn-sm"
                    @click=${() => openSendSuSUpdateLinkDialog(person._id, false)}
                  >
                    <span
                      class="icon-[material-symbols--forward-to-inbox-outline-rounded] text-lg"
                      aria-hidden="true"
                    ></span>
                    Aktualisierungslink senden
                  </button>
                  <button class="btn btn-outline btn-sm" @click=${() => openSendSuSUpdateLinkDialog(person._id, true)}>
                    <span
                      class="icon-[material-symbols--schedule-send-outline-rounded] text-lg"
                      aria-hidden="true"
                    ></span>
                    Aktualisierungslink mit Frist senden
                  </button>`
              : nothing
          }
          ${
            // the assistant writes through the booking upsert, which checks the same permission
            user.hasPermission(Permission.EDIT_BOOKINGS, ANY_CONTEXT)
              ? html`<button
                  class="btn btn-primary btn-sm"
                  @click=${async () => {
                    if (await openClassAdmissionDialog(person._id)) void SgSnackbar.show('Klassenbuchung angelegt');
                  }}
                >
                  <span class="icon-[material-symbols--school-rounded] text-lg" aria-hidden="true"></span>
                  In Klasse aufnehmen
                </button>`
              : nothing
          }
        `,
      })}
      <div class="grid flex-auto grid-cols-[1fr_4fr] gap-3">
        ${SgFormGridEntry({
          label: 'Bewerbungsstatus',
          render: html`
            <div class="flex gap-4">
              ${DropdownSelectionInput({
                ...form.field('schuelerDaten', 'susBewerberStatus'),
                placeholder: 'Kein Status ausgewählt',
                placeholderMode: 'hide',
                selectables: Object.values(SuSBewerberStatus),
                renderItem: status => html`
                  <div class="flex flex-col gap-2">
                    <p class="font-bold">${status}</p>
                    <p class="text-sm text-base-content/70">${getSuSBewerberStatusDescription(status)}</p>
                  </div>
                `,
              })}
              ${getSuSBewerberStatusDescription(form.document.schuelerDaten?.susBewerberStatus as SuSBewerberStatus)}
            </div>
          `,
        })}
        ${SgFormGridEntry({
          label: 'Datum der Bewerbung',
          render: html`
            <p>
              ${
                person.schuelerDaten?.bewerbungsDate
                  ? formatCalendarDay(person.schuelerDaten.bewerbungsDate, 'dd.MM.yyyy')
                  : 'Das Kind wurde nicht über das Anmeldeformular beworben.'
              }
            </p>
          `,
        })}
        ${SgFormGridEntry({
          label: 'Vorherige Schule',
          render: html` <p>${elementarySchool?.[0]?.name ?? 'Andere Grundschule'}</p> `,
        })}
        ${
          person.schuelerDaten?.otherSchool
            ? SgFormGridEntry({
                label: 'Name der Anderen Schule',
                render: html` <p>${person.schuelerDaten.otherSchool}</p>`,
              })
            : nothing
        }
        ${
          person.schuelerDaten?.schoolyearsAfterElementary
            ? SgFormGridEntry({
                label: 'Aktuelle Klassenstufe',
                render: html` <p>${person.schuelerDaten.schoolyearsAfterElementary}</p> `,
              })
            : nothing
        }
        ${SgFormGridEntry({
          label: 'Beworbene Klassenstufe',
          render: html` <p>${person.schuelerDaten?.wantedKlassenstufe}</p>`,
        })}
        ${SgFormGridEntry({
          label: 'Aktuelle Klassenstufe zum Zeitpunkt der Bewerbung',
          render: TextInput({
            ...form.field('schuelerDaten', 'currentKlassenstufeAtBewerbungsDate'),
            placeholder: 'Keine Angabe',
            placeholderMode: 'hide',
          }),
        })}
        ${
          // we only need to check one as the rest is implicitly checked by the entry form
          person.schuelerDaten?.lastGradeGerman
            ? SgFormGridEntry({
                label: 'Grundschulnoten',
                render: html`
                  <div class="grid grid-cols-[1fr_4fr] gap-3">
                    <p bold>Deutsch</p>
                    <p>${person.schuelerDaten.lastGradeGerman}</p>
                    <p bold>Mathematik</p>
                    <p>${person.schuelerDaten.lastGradeMath}</p>
                    <p bold>Sachunterricht</p>
                    <p>${person.schuelerDaten.lastGradeSachunterricht}</p>
                  </div>
                `,
              })
            : nothing
        }
        ${SgFormGridEntry({
          label: 'Zuhause gesprochene Sprache',
          render: html` <p>${person.schuelerDaten?.languageAtHomeIsGerman ? 'Deutsch' : 'Andere Sprache'}</p>`,
        })}
        ${SgFormGridEntry({
          label: 'Geschwister an Martinus Schulen',
          render: html` <p>${person.schuelerDaten?.hasSiblingsAtMartinusSchools ? 'Ja' : 'Nein'}</p>`,
        })}
        ${
          person.schuelerDaten?.sorgerechtsurteilFileID
            ? SgFormGridEntry({
                label: 'Eventuelles Sorgerechtsurteil',
                render: html`
                  <a
                    class="link link-primary"
                    href=${person.schuelerDaten.sorgerechtsurteilFile?.getDownloadLink()}
                    target="_blank"
                  >
                    ${person.schuelerDaten.sorgerechtsurteilFile?.meta?.fileName}
                  </a>
                `,
              })
            : nothing
        }
        ${
          person.schuelerDaten?.learningDisabilitiesFileID
            ? SgFormGridEntry({
                label: 'Eventuelle Lernbehinderungen',
                render: html`
                  <a
                    class="link link-primary"
                    href=${person.schuelerDaten.learningDisabilitiesFile?.getDownloadLink()}
                    target="_blank"
                  >
                    ${person.schuelerDaten.learningDisabilitiesFile?.meta?.fileName}
                  </a>
                `,
              })
            : nothing
        }
        ${
          person.schuelerDaten?.primarySchoolTranscriptsFiles?.length
            ? SgFormGridEntry({
                label: 'Zeugnisse und weitere Dateien',
                render: html`
                  ${person.schuelerDaten.primarySchoolTranscriptsFiles.map(
                    (file, index) => html`
                      <div class="flex items-center gap-2">
                        ${index + 1}.
                        <a class="link link-primary" href=${file.getDownloadLink()} target="_blank">
                          <p>${file.meta?.fileName}.${file.meta?.extension}</p>
                        </a>
                      </div>
                    `,
                  )}
                `,
              })
            : nothing
        }
        ${SgFormGridEntry({
          label: 'Interesse an herkunftssprachlichen Kursen',
          render: html` <p>${person.schuelerDaten?.interestInNativeLanguageCourses ?? 'Nein'}</p> `,
        })}
        ${SgFormGridEntry({
          label: 'Interesse an Nachmittagsbetreuung',
          render: html` <p>${person.schuelerDaten?.interestInWholeDaySchooling ? 'Ja' : 'Nein'}</p> `,
        })}
        ${
          isNotNullish(getEinschulungsJahr(person.schuelerDaten))
            ? SgFormGridEntry({
                label: 'Einschulungsjahr',
                render: html` <p>${getEinschulungsJahr(person.schuelerDaten)}</p> `,
              })
            : nothing
        }
        ${SgFormGridEntry({
          label: 'Teilnahme am Religionsunterricht',
          render: html` <p>${person.schuelerDaten?.participationReligion ? 'Ja' : 'Nein'}</p> `,
        })}
        ${SgFormGridEntry({
          label: 'Zuzugsdatum',
          render: html`
            ${
              person.schuelerDaten?.zuzugsDate
                ? formatCalendarDay(person.schuelerDaten.zuzugsDate, 'dd.MM.yyyy')
                : 'Keine Angabe'
            }
          `,
        })}
      </div>
      <h4 class="border-b border-base-300 pb-1 font-semibold">Motivation</h4>

      <p>${person.schuelerDaten?.motivation || 'Keine Motivation angegeben'}</p>
      ${
        person.schuelerDaten?.schulwechselMotivation
          ? html`
              <h4 class="border-b border-base-300 pb-1 font-semibold">Schulwechsel Grund</h4>

              <p>${person.schuelerDaten.schulwechselMotivation}</p>
            `
          : nothing
      }
      <h4 class="border-b border-base-300 pb-1 font-semibold">Gesprächstermin</h4>
      ${
        interviewSlotLoading
          ? html`<span class="loading loading-spinner"></span>`
          : interviewSlot
            ? html`
                <div class="card bg-base-200 p-3">
                  <div class="grid grid-cols-[1fr_4fr] gap-3">
                    ${SgFormGridEntry({
                      label: 'Datum',
                      render: html`<p>
                        ${interviewSlot.startDate ? format(interviewSlot.startDate, 'dd.MM.yyyy') : ''}
                      </p>`,
                    })}
                    ${SgFormGridEntry({
                      label: 'Uhrzeit',
                      render: html`<p>
                        ${interviewSlot.startDate ? format(interviewSlot.startDate, 'HH:mm') : ''} -
                        ${interviewSlot.endDate ? format(interviewSlot.endDate, 'HH:mm') : ''}
                      </p>`,
                    })}
                    ${
                      interviewSlot.interviewer
                        ? SgFormGridEntry({
                            label: 'Gesprächsführer',
                            render: html`
                              <a class="link link-primary" href="/personen/${interviewSlot.interviewerID}">
                                ${interviewSlot.interviewer.profilDaten?.stammdaten?.vorname}
                                ${interviewSlot.interviewer.profilDaten?.stammdaten?.nachname}
                              </a>
                            `,
                          })
                        : nothing
                    }
                  </div>
                </div>
              `
            : html`
                <div class="card bg-base-200 p-3">
                  <p class="text-base-content/70">Es konnte kein Gesprächstermin für diesen Schüler gefunden werden.</p>
                </div>
              `
      }
      <h4 class="border-b border-base-300 pb-1 font-semibold">Interne Notizen</h4>
      ${TextareaInput({
        ...form.field('schuelerDaten', 'internalNotes'),
        placeholder: 'Keine internen Notizen angegeben',
        placeholderMode: 'hide',
      })}
      <h4 class="border-b border-base-300 pb-1 font-semibold">Ablehnungsgrund</h4>
      ${TextareaInput({
        ...form.field('schuelerDaten', 'ablehnungGrund'),
        placeholder: 'Kein Ablehnungsgrund angegeben',
        placeholderMode: 'hide',
      })}

      <div class="flex flex-row justify-end">
        <button class="btn btn-primary btn-lg" @click=${onSave}>
          <span class="icon-[material-symbols--save-outline-rounded] size-5"></span>
          Speichern
        </button>
      </div>
    </div>
  `;
});
